/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import { connect, MqttClient } from "mqtt";
import { EventbusService } from "./eventbus-service";

export interface IMQTTServiceConfig {
  brokerURL: string;
  username?: string;
  password?: string;
}

export class MqttService {
  private static instance: MqttService;
  private client: MqttClient;
  private topics: string[] = [];
  private readonly eventBus: any;
  private constructor(options: IMQTTServiceConfig) {
    const opt: any = {
      clean: true,
    };
    if (options.username) {
      opt.username = options.username;
    }
    if (options.password) {
      opt.password = options.password;
    }
    this.client = connect(options.brokerURL, opt);
    this.eventBus = EventbusService.getInstance()?.getEventBus();

    this.client.on("connect", this.eventHandler.bind(this, "connect"));
    this.client.on("reconnect", this.eventHandler.bind(this, "reconnect"));
    this.client.on("close", this.eventHandler.bind(this, "close"));
    this.client.on("disconnect", this.eventHandler.bind(this, "disconnect"));
    this.client.on("offline", this.eventHandler.bind(this, "offline"));
    this.client.on("error", this.eventHandler.bind(this, "error"));
    this.client.on("message", this.eventHandler.bind(this, "message"));
  }

  public static getInstance(
    options: IMQTTServiceConfig | any = {}
  ): MqttService {
    if (!MqttService.instance) {
      MqttService.instance = new MqttService(options);
    }
    return MqttService.instance;
  }

  getEventBus() {
    return this.eventBus;
  }

  public subscribeTopics(topics: string[]) {
    // for (const topic of topics) {
    //   const found = this.topics.find(top => top === topic);
    //   if (!found) {
    //     this.topics.push(topic);
    //   }
    // }
    this.client.subscribe(topics);
  }

  public unsubscribeTopics(topics: string[]) {
    // this.topics = this.topics.filter(top => !topics.includes(top));
    this.client.unsubscribe(topics);
  }

  public getSubscribedTopics() {
    return this.topics;
  }

  public publishTopic(
    topic: string,
    message: string | any,
    qos: 0 | 1 | 2 = 2
  ) {
    let _msg = message;
    if (typeof _msg !== "string") {
      _msg = JSON.stringify(_msg);
    }
    this.client.publish(topic, _msg, { qos: qos });
  }

  public disconnect(force = false) {
    return this.client?.end(force);
  }

  async eventHandler(event: string, topic?: string, msg?: any) {
    switch (event) {
      case "connect":
        console.log("[mqtt] | connect");
        this.eventBus.emit("connect", this, msg);
        break;
      case "reconnect":
        console.log("[mqtt] | reconnect");
        this.eventBus.emit("reconnect", this, msg);
        break;
      case "close":
        console.log("[mqtt] | close", topic, msg);
        this.eventBus.emit("close", this, msg);
        break;
      case "offline":
        console.log("[mqtt] | offline");
        this.eventBus.emit("offline", this, msg);
        break;
      case "disconnect":
        console.log("[mqtt] | disconnect");
        this.eventBus.emit("disconnect", this, msg);
        break;
      case "error":
        console.log("[mqtt] | error");
        this.eventBus.emit("error", this, msg);
        break;
      case "message":
        // console.log('[mqtt] | message');
        msg = msg?.toString();
        try {
          msg = JSON.parse(msg);
        } catch (err: any) {
          // eslint-disable-next-line no-empty
        }
        this.eventBus.emit("message", this, {
          topic: topic,
          message: msg,
        });
        break;
      default:
        console.log(`[mqtt] unhandled event ${event}`);
    }
  }
}
